import * as React from 'react';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import Skeleton from '@mui/material/Skeleton';
import IconButton from '@mui/material/IconButton';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import { Box, Pagination, Select, MenuItem, Divider, Tooltip } from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import useI18n from '../../../hooks/useI18n';
import { SimpleTableBody } from './TableBodyContent';
import { PriceListTableBody } from './PriceListTableBody';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import Checkbox from '@mui/material/Checkbox';
import CellType from '../../../constants/type';

const groupByCategoryAndBrand = (products, language) => {
    const groupedData = {};

    if (!products) {
        return groupedData;
    }

    products.forEach(product => {
        const LEVEL1 = product[`LEVEL1${language === 'ru' ? 'RUS' : ''}`] 
            ? product[`LEVEL1${language === 'ru' ? 'RUS' : ''}`] 
            : product['LEVEL1'];
        const LEVEL2 = product[`LEVEL2${language === 'ru' ? 'RUS' : ''}`] 
            ? product[`LEVEL2${language === 'ru' ? 'RUS' : ''}`] 
            : product['LEVEL2'];

        if (!groupedData[LEVEL1]) {
            groupedData[LEVEL1] = {};
        }

        if (!groupedData[LEVEL1][LEVEL2]) {
            groupedData[LEVEL1][LEVEL2] = [];
        }

        groupedData[LEVEL1][LEVEL2].push(product);
    });

    return groupedData;
};

export default function TableData(prop) {
    const {
        columns,
        rows,
        setClickRow,
        isPointCursor,
        cellBtnActionByType,
        isPriceList = false,
        ownPagination = false,
        onPageChange,
        onRowsPerPageChange,
        isShowBrand = true,
        isShowCategory = true,
        isGrouped = true,
        rowsPerPageV = 100,
        tableEmptyText = 'No data available',
        openFilters,
        filtersOpen = false,
        isBasket = false,
    } = prop;

    const [page, setPage] = React.useState(0);
    const [openImage, setOpenImage] = React.useState(false);
    const [image, setImage] = React.useState('');
    const [rowsPerPage] = React.useState(rowsPerPageV);
    const [groupedData, setGroupedData] = React.useState({});
    const { l, language } = useI18n();

    const paginationData = React.useCallback((newPage) => {
        if (isPriceList) {
            setGroupedData(
                groupByCategoryAndBrand(
                    rows?.slice(
                        newPage * rowsPerPage,
                        newPage * rowsPerPage + rowsPerPage
                    ),
                    language
                )
            );
        }
    }, [isPriceList, rows, rowsPerPage, language]);

    React.useEffect(() => {
        paginationData(0);
        setPage(ownPagination?.page || 0);
    }, [rows, ownPagination?.page]);

    const handleChangePage = (event, newPage) => {
        window.scrollTo(0, 0);
        setPage(newPage - 1);
        paginationData(newPage);
    };

    const handleClickOpenImage = (image) => {
        setImage(image);
        setOpenImage(true);
    };

    const handleCloseImage = () => {
        setOpenImage(false);
    };

    const paginationInfo = () => {
        const currentPage = page || 1;
        const startItem = (currentPage - 1) * (ownPagination?.pageSize || 0) + 1;
        const endItem = Math.min(
            currentPage * (ownPagination?.pageSize || 0),
            ownPagination?.totalItems || 0
        );

        return (
            <Typography variant="p">
                {`${l('Shown')} ${startItem}-${endItem} ${l('of')} ${ownPagination?.totalItems || 0}`}
            </Typography>
        );
    };

    const paginationInfoOff = () => {
        const currentPage = page;
        const startItem = currentPage * (rowsPerPage || 0) + 1;
        const endItem = Math.min(
            (currentPage + 1) * (rowsPerPage || 0),
            rows?.length || 0
        );

        return (
            <Typography variant="p">
                {`${l('Shown')} ${startItem}-${endItem} ${l('of')} ${rows?.length || 0}`}
            </Typography>
        );
    };

    const countOfPages = Math.ceil((rows ? rows?.length : 0) / rowsPerPage);

    const renderTableBody = () => {
        if (isPriceList) {
            return (
                <PriceListTableBody
                    rows={rows}
                    columns={columns}
                    groupedData={groupedData}
                    isGrouped={isGrouped}
                    isShowCategory={isShowCategory}
                    isShowBrand={isShowBrand}
                    isPointCursor={isPointCursor}
                    setClickRow={setClickRow}
                    cellBtnActionByType={cellBtnActionByType}
                    l={l}
                    handleClickOpenImage={handleClickOpenImage}
                    language={language}
                    tableEmptyText={l(tableEmptyText)}
                />
            );
        }

        return (
            <SimpleTableBody
                rows={rows}
                columns={columns}
                page={page}
                rowsPerPage={ownPagination?.pageSize || rowsPerPage}
                isPointCursor={isPointCursor}
                setClickRow={setClickRow}
                cellBtnActionByType={cellBtnActionByType}
                l={l}
                handleClickOpenImage={handleClickOpenImage}
                language={language}
                tableEmptyText={l(tableEmptyText)}
                isBasket={isBasket}
            />
        );
    };

    return (
        <Paper sx={{ width: '100%', overflow: 'hidden' }}>
            {ownPagination && (
                <>
                    <Box display='flex' alignItems='center'>
                        {openFilters && (
                            <Box marginRight={1}>
                                <Tooltip title={l('Filtration')}>
                                    <IconButton onClick={openFilters} color={filtersOpen ? 'primary' : 'default'}>
                                        <FilterAltIcon/>
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        )}
                        <Box marginLeft={1} marginTop={1} marginBottom={1}>
                            {`${l('Rows per page')}:`}
                            <Select
                                variant="standard"
                                sx={{ marginLeft: 1, height: '25px' }}
                                value={ownPagination?.pageSize || 100}
                                onChange={onRowsPerPageChange}
                            >
                                {ownPagination.rowsPerPageOptions.map((option) => (
                                    <MenuItem key={option} value={option}>
                                        {option}
                                    </MenuItem>
                                ))}
                            </Select>
                        </Box>
                    </Box>
                    <Divider />
                </>
            )}

            <TableContainer sx={{ height: '100%' }}>
                <Table sx={{ width: '100%' }} stickyHeader aria-label="sticky table">
                    <TableHead>
                        <TableRow>
                            {columns ? (
                                columns.map((column) => (
                                    <TableCell
                                        sx={{
                                            py: 1,
                                            px: { xs: 0.5, xl: 1 },
                                            fontSize: { xs: '0.75rem', xl: '0.875rem' },
                                            lineHeight: 1.25,
                                        }}
                                        key={column.id}
                                        align={column.align}
                                        style={{ minWidth: column.minWidth, fontWeight: 'bold' }}
                                    >
                                        {column.type === CellType.CHECKBOX && column.headerCheckboxProps ? (
                                            <Checkbox
                                                checked={Boolean(column.headerCheckboxProps.checked)}
                                                indeterminate={Boolean(column.headerCheckboxProps.indeterminate)}
                                                disabled={Boolean(column.headerCheckboxProps.disabled)}
                                                onChange={column.headerCheckboxProps.onChange}
                                                inputProps={{
                                                    'aria-label': column.headerCheckboxProps.ariaLabel || column.label,
                                                }}
                                            />
                                        ) : column.headerTooltip ? (
                                            <Tooltip title={column.headerTooltip} arrow>
                                                <span>{column.label}</span>
                                            </Tooltip>
                                        ) : column.label}
                                    </TableCell>
                                ))
                            ) : (
                                <TableCell>
                                    <Skeleton variant="rounded" width={60} height={60} />
                                </TableCell>
                            )}
                        </TableRow>
                    </TableHead>
                    <TableBody>{renderTableBody()}</TableBody>
                </Table>
            </TableContainer>

            {ownPagination ? (
                <Box
                    display="flex"
                    flexWrap="wrap"
                    justifyContent="space-between"
                    alignItems="center"
                    padding="10px 0"
                >
                    <Box marginLeft={1}>{paginationInfo()}</Box>
                    <Pagination
                        count={ownPagination?.totalPages || 0}
                        shape="rounded"
                        onChange={onPageChange}
                    />
                    <Box width="20%"></Box>
                </Box>
            ) : countOfPages > 1 ? (
                <Box
                    display="flex"
                    flexWrap="wrap"
                    justifyContent="space-between"
                    alignItems="center"
                    padding="10px 0"
                >
                    <Box marginLeft={1}>{paginationInfoOff()}</Box>
                    <Pagination
                        count={countOfPages}
                        shape="rounded"
                        onChange={handleChangePage}
                    />
                    <Box width="20%"></Box>
                </Box>
            ) : null}

            <Dialog
                open={openImage}
                onClose={handleCloseImage}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogActions>
                    <IconButton onClick={handleCloseImage}>
                        <ClearIcon />
                    </IconButton>
                </DialogActions>
                <DialogContent>
                    <img src={image} alt="" style={{ width: '100%' }} />
                </DialogContent>
            </Dialog>
        </Paper>
    );
}